import { useState } from "react";
import { toast } from "sonner";
import { Moon, CalendarClock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { SealedTomorrow } from "@/v2/Pull";

const FEEL = [["great", "Good day"], ["okay", "Okay day"], ["hard", "Hard day"]];
const THANKS = { great: "Hold on to that. Sleep well.", okay: "Okay counts. Rest up.", hard: "Thank you for telling me. Tomorrow I'll go gently." };

export function Tomorrow({ t }) {
  if (!t) return null;
  return (
    <div className="card p-5" data-testid="tomorrow-card">
      <span className="eyebrow flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />Tomorrow</span>
      {t.project ? <>
        <p className="font-display text-lg mt-2 leading-snug" data-testid="tomorrow-text">{t.text || "The next step in your project"}</p>
        <p className="text-xs text-stone-600 mt-1.5">{t.project} · week {t.week} of {t.weeks}: {t.milestone}</p>
      </> : <p className="font-display text-lg mt-2" data-testid="tomorrow-text">{t.text}</p>}
      {t.review && <p className="text-xs text-stone-700 mt-2 font-semibold">Plus: your weekly review is ready to write.</p>}
      <p className="text-[11px] text-stone-500 mt-3">The final plan depends on how you wake up.</p>
    </div>
  );
}

function TomorrowBlock({ t }) {
  return (
    <div className="space-y-4">
      <SealedTomorrow env={t?.envelope} />
      {!t?.envelope && <Tomorrow t={t} />}
    </div>
  );
}

export function EveningWrap({ day, tomorrow, onWrap }) {
  const [feel, setFeel] = useState(null);
  const [line, setLine] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post("/v2/today/wrap", { feeling: feel, line: line || null }); onWrap(data); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (day.wrap) return (
    <div className="space-y-4">
      <div className="card p-5 bg-[#2D2926] border-[#2D2926] text-stone-100" data-testid="evening-done">
        <span className="eyebrow !text-stone-300 flex items-center gap-1.5"><Moon className="h-3.5 w-3.5" />Today, wrapped</span>
        <p className="font-display text-lg mt-2">{THANKS[day.wrap.feeling]}</p>
        {day.wrap.line && <p className="text-sm text-stone-300 mt-1 italic">"{day.wrap.line}"</p>}
      </div>
      <TomorrowBlock t={tomorrow} />
    </div>
  );
  return (
    <div className="card p-5" data-testid="evening-wrap">
      <span className="eyebrow flex items-center gap-1.5"><Moon className="h-3.5 w-3.5" />Evening wrap-up</span>
      <p className="font-display text-xl mt-2">How did today go?</p>
      <div className="grid grid-cols-3 gap-2 mt-4">
        {FEEL.map(([k, l]) => (
          <button key={k} onClick={() => setFeel(k)} data-testid={`wrap-${k}`}
            className={`chip h-11 rounded-full text-sm font-medium border ${feel === k ? "bg-stone-900 text-white border-stone-900" : "border-stone-200 bg-white hover:border-stone-400"}`}>{l}</button>
        ))}
      </div>
      {feel && <>
        <input value={line} onChange={(e) => setLine(e.target.value)} maxLength={200} placeholder="One line about today (optional)" data-testid="wrap-line"
          className="mt-3 w-full h-11 px-4 rounded-full border border-stone-200 text-sm bg-white" />
        <button onClick={save} disabled={busy} data-testid="wrap-save" className="btn-ink mt-3 w-full h-11 rounded-full text-sm font-semibold disabled:opacity-50">{busy ? "Saving…" : "Close the day"}</button>
      </>}
      <p className="text-[11px] text-stone-500 mt-3">CAMRYN remembers this tomorrow morning.</p>
    </div>
  );
}
